import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  templates: [], // Array of saved shipment templates
  // Each template: { id, name, origin, destination, parcelWeight, parcelType, volume, createdAt, lastUsed }
};

const shipmentTemplatesSlice = createSlice({
  name: 'shipmentTemplates',
  initialState,
  reducers: {
    /**
     * Replace all templates (useful when loading from server)
     */
    setTemplates(state, action) {
      state.templates = action.payload || [];
    },

    /**
     * Add a new shipment template
     */
    addTemplate(state, action) {
      const template = {
        ...action.payload,
        id: action.payload.id || `template_${Date.now()}`,
        createdAt: action.payload.createdAt || new Date().toISOString(),
        lastUsed: action.payload.lastUsed || null,
      };

      // Check for duplicates based on origin + destination
      const normalize = (val) => {
        if (!val) return '';
        if (typeof val === 'string') return val;
        return val.label || val.address || val.formatted_address || '';
      };

      const exists = state.templates.find((t) => {
        return (
          normalize(t.origin) === normalize(template.origin) &&
          normalize(t.destination) === normalize(template.destination)
        );
      });

      if (!exists) {
        state.templates.unshift(template);
        // Keep max 20 templates
        if (state.templates.length > 20) {
          state.templates.pop();
        }
      }
    },

    /**
     * Update an existing template
     */
    updateTemplate(state, action) {
      const { id, ...updates } = action.payload;
      const idx = state.templates.findIndex((t) => t.id === id);
      if (idx !== -1) {
        state.templates[idx] = {
          ...state.templates[idx],
          ...updates,
          updatedAt: new Date().toISOString(),
        };
      }
    },

    /**
     * Remove a template by id
     */
    removeTemplate(state, action) {
      state.templates = state.templates.filter((t) => t.id !== action.payload);
    },

    /**
     * Mark template as recently used
     */
    useTemplate(state, action) {
      const idx = state.templates.findIndex((t) => t.id === action.payload);
      if (idx !== -1) {
        state.templates[idx].lastUsed = new Date().toISOString();
        // Move to front of list
        const [template] = state.templates.splice(idx, 1);
        state.templates.unshift(template);
      }
    },

    /**
     * Clear all templates
     */
    clearTemplates(state) {
      state.templates = [];
    },

    /**
     * Logout - reset to initial state
     */
    logout: () => initialState,
  },
});

export const { addTemplate, updateTemplate, removeTemplate, useTemplate, clearTemplates, logout } =
  shipmentTemplatesSlice.actions;

export default shipmentTemplatesSlice.reducer;
